/**
 * usePendingFollowUps (headless)
 *
 * Cross-job list of POST_COMPLETION workflow rows still pending for the
 * current user, plus a count hook suitable for a nav badge.
 *
 * "Pending" is derived from columns only, no status enum:
 *   has_post_completion_steps = true
 *   AND post_completion_completed_at IS NULL
 *   AND post_completion_cancelled_at IS NULL
 *
 * Each app supplies its own SupabaseClient<Database>.
 */

import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "../database.types";
import type {
  JobWorkflow,
  StepResponse,
  WorkflowStatus,
  WorkflowStep,
} from "../types/workflows";

export interface PendingFollowUpWorkflow extends JobWorkflow {
  job: {
    id: string;
    title: string | null;
    job_status: string | null;
    completed_at: string | null;
    customer_id: string | null;
  };
}

export interface UsePendingFollowUpsOptions {
  companyId?: string | null;
  isManagerOrOwner?: boolean;
}

export function usePendingFollowUps(
  supabase: SupabaseClient<Database>,
  userId: string | null | undefined,
  options?: UsePendingFollowUpsOptions,
) {
  const companyId = options?.companyId;
  const isManagerOrOwner = options?.isManagerOrOwner;

  return useQuery({
    queryKey: ["pendingFollowUps", userId, companyId, isManagerOrOwner],
    queryFn: async (): Promise<PendingFollowUpWorkflow[]> => {
      if (!userId) return [];

      const getSafeSession = (supabase as unknown as Record<string, unknown>).getSafeSession;
      if (typeof getSafeSession === "function") {
        const session = await (getSafeSession as () => Promise<unknown>)();
        if (!session) return [];
      } else if (supabase.auth) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return [];
      }

      let query = supabase
        .from("tradie_workflows")
        .select(
          `
          *,
          workflow_templates!inner ( title, description ),
          job:jobs!inner (
            id, title, job_status, completed_at, customer_id, assigned_trade_id
          )
          `,
        );

      if (isManagerOrOwner && companyId) {
        query = query.eq("job.assigned_trade_id", companyId);
      } else {
        query = query.eq("post_completion_assignee_id", userId);
      }

      query = query
        .eq("has_post_completion_steps", true)
        .is("post_completion_completed_at", null)
        .is("post_completion_cancelled_at", null);

      const { data, error } = await query.order("post_completion_due_at", {
        ascending: true,
        nullsFirst: false,
      });

      if (error) throw error;

      type TradieWorkflowRow = Database["public"]["Tables"]["tradie_workflows"]["Row"];
      interface QueryRow extends TradieWorkflowRow {
        workflow_templates: Pick<
          Database["public"]["Tables"]["workflow_templates"]["Row"],
          "title" | "description" | "completion_gate_mode" | "version"
        > | null;
        job: Pick<
          Database["public"]["Tables"]["jobs"]["Row"],
          "id" | "title" | "job_status" | "completed_at" | "customer_id"
        > | null;
      }

      return (data as unknown as QueryRow[] ?? []).map((row) => ({
        ...row,
        workflow: (row.workflow ?? []) as WorkflowStep[],
        response_data: (row.response_data ?? {}) as Record<
          string,
          StepResponse
        >,
        status: (row.status ?? "pending") as WorkflowStatus,
        has_post_completion_steps: row.has_post_completion_steps ?? false,
        in_progress_completed_at: row.in_progress_completed_at ?? null,
        post_completion_completed_at: row.post_completion_completed_at ?? null,
        post_completion_due_at: row.post_completion_due_at ?? null,
        post_completion_cancelled_at: row.post_completion_cancelled_at ?? null,
        post_completion_assignee_id: row.post_completion_assignee_id ?? null,
      })) as PendingFollowUpWorkflow[];
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

export function usePendingFollowUpsCount(
  supabase: SupabaseClient<Database>,
  userId: string | null | undefined,
  options?: UsePendingFollowUpsOptions,
) {
  const companyId = options?.companyId;
  const isManagerOrOwner = options?.isManagerOrOwner;

  return useQuery({
    queryKey: ["pendingFollowUpsCount", userId, companyId, isManagerOrOwner],
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;

      const getSafeSession = (supabase as unknown as Record<string, unknown>).getSafeSession;
      if (typeof getSafeSession === "function") {
        const session = await (getSafeSession as () => Promise<unknown>)();
        if (!session) return 0;
      } else if (supabase.auth) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return 0;
      }

      if (isManagerOrOwner && companyId) {
        const { count, error } = await supabase
          .from("tradie_workflows")
          .select(
            // Must mirror the !inner joins in usePendingFollowUps exactly. A row
            // whose template or job is missing (or hidden by RLS) is dropped from
            // the list; without the same joins here it would still be counted,
            // and the nav badge would read higher than the page it links to.
            "id, workflow_templates!inner(id), job:jobs!inner(id, assigned_trade_id)",
            { count: "exact", head: true },
          )
          .eq("job.assigned_trade_id", companyId)
          .eq("has_post_completion_steps", true)
          .is("post_completion_completed_at", null)
          .is("post_completion_cancelled_at", null);

        if (error) throw error;
        return count ?? 0;
      }

      const { count, error } = await supabase
        .from("tradie_workflows")
        .select(
          // Same join parity requirement as the company branch above.
          "id, workflow_templates!inner(id), job:jobs!inner(id)",
          { count: "exact", head: true },
        )
        .eq("post_completion_assignee_id", userId)
        .eq("has_post_completion_steps", true)
        .is("post_completion_completed_at", null)
        .is("post_completion_cancelled_at", null);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}
